import React, { createContext, useContext, useState, useEffect } from 'react'

interface BrandSettings {
  enabled: boolean
  components: {
    buttons: boolean
    cards: boolean
    inputs: boolean
    global: boolean
  }
  overrides: Record<string, boolean>
}

interface BrandContextType {
  settings: BrandSettings
  updateSettings: (settings: Partial<BrandSettings>) => void
  toggleComponent: (component: keyof BrandSettings['components']) => void
  setComponentOverride: (componentId: string, enabled: boolean) => void
  isComponentBranded: (component: keyof BrandSettings['components'], componentId?: string) => boolean
}

const defaultSettings: BrandSettings = {
  enabled: true,
  components: {
    buttons: true,
    cards: true,
    inputs: true,
    global: true
  },
  overrides: {}
}

const BrandContext = createContext<BrandContextType | undefined>(undefined)

export function BrandProvider({ children }: { children: React.ReactNode }) {
  const [settings, setSettings] = useState<BrandSettings>(() => {
    const saved = localStorage.getItem('brand-settings')
    return saved ? JSON.parse(saved) : defaultSettings
  })

  useEffect(() => {
    localStorage.setItem('brand-settings', JSON.stringify(settings))
  }, [settings])

  const updateSettings = (newSettings: Partial<BrandSettings>) => {
    setSettings(prev => ({ ...prev, ...newSettings }))
  }

  const toggleComponent = (component: keyof BrandSettings['components']) => {
    setSettings(prev => ({
      ...prev,
      components: {
        ...prev.components,
        [component]: !prev.components[component]
      }
    }))
  }

  const setComponentOverride = (componentId: string, enabled: boolean) => {
    setSettings(prev => ({
      ...prev,
      overrides: {
        ...prev.overrides,
        [componentId]: enabled
      }
    }))
  }

  const isComponentBranded = (component: keyof BrandSettings['components'], componentId?: string): boolean => {
    if (!settings.enabled) return false
    
    // Check component-specific override first
    if (componentId && settings.overrides[componentId] !== undefined) {
      return settings.overrides[componentId]
    }
    
    // Check global brand setting
    if (settings.components.global) return true
    
    // Check component-specific setting
    return settings.components[component]
  }

  return (
    <BrandContext.Provider value={{
      settings,
      updateSettings,
      toggleComponent,
      setComponentOverride,
      isComponentBranded
    }}>
      {children}
    </BrandContext.Provider>
  )
}

export function useBrand() {
  const context = useContext(BrandContext)
  if (context === undefined) {
    // Return safe defaults instead of throwing an error
    return {
      settings: {
        enabled: false,
        components: { buttons: false, cards: false, inputs: false, global: false },
        overrides: {}
      },
      updateSettings: () => {},
      toggleComponent: () => {},
      setComponentOverride: () => {},
      isComponentBranded: () => false
    }
  }
  return context
}